import { 
  File, 
  FileText, 
  FileImage, 
  FileSpreadsheet, 
  FileCode, 
  FileArchive, 
  FileVideo, 
  FileAudio,
  FilePieChart,
  FileType
} from 'lucide-react';

/**
 * Dosya uzantısına göre uygun ikonu ve rengini döndürür
 * @param {string} filename - Dosya adı
 * @returns {Object} - { Icon: Component, color: string, bgColor: string }
 */
export const getFileIcon = (filename) => {
  if (!filename) {
    return {
      Icon: File,
      color: 'text-gray-600',
      bgColor: 'from-gray-100 to-gray-200',
      label: 'Dosya'
    };
  }

  const extension = filename.split('.').pop()?.toLowerCase();

  // Resim dosyaları
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico', 'tiff', 'heic'];
  if (imageExtensions.includes(extension)) {
    return {
      Icon: FileImage,
      color: 'text-purple-600',
      bgColor: 'from-purple-100 to-pink-100',
      label: 'Resim'
    };
  }

  // Word dosyaları
  const wordExtensions = ['doc', 'docx', 'odt', 'rtf'];
  if (wordExtensions.includes(extension)) {
    return {
      Icon: FileText,
      color: 'text-blue-600',
      bgColor: 'from-blue-100 to-blue-200',
      label: 'Word Belgesi'
    };
  }

  // Excel dosyaları
  const excelExtensions = ['xls', 'xlsx', 'ods', 'csv'];
  if (excelExtensions.includes(extension)) {
    return {
      Icon: FileSpreadsheet,
      color: 'text-green-600',
      bgColor: 'from-green-100 to-emerald-100',
      label: 'Excel Tablosu'
    };
  }

  // PowerPoint dosyaları
  const powerpointExtensions = ['ppt', 'pptx', 'odp'];
  if (powerpointExtensions.includes(extension)) {
    return {
      Icon: FilePieChart,
      color: 'text-orange-600',
      bgColor: 'from-orange-100 to-red-100',
      label: 'Sunum'
    };
  }

  // PDF dosyaları
  if (extension === 'pdf') {
    return {
      Icon: FileType,
      color: 'text-red-600',
      bgColor: 'from-red-100 to-red-200',
      label: 'PDF'
    };
  }

  // Kod dosyaları
  const codeExtensions = [
    'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'cs', 'php', 
    'rb', 'go', 'swift', 'kt', 'rs', 'html', 'css', 'scss', 'sass', 
    'json', 'xml', 'yaml', 'yml', 'sql', 'sh', 'bat'
  ];
  if (codeExtensions.includes(extension)) {
    return {
      Icon: FileCode,
      color: 'text-cyan-600',
      bgColor: 'from-cyan-100 to-blue-100',
      label: 'Kod Dosyası'
    };
  }

  // Arşiv dosyaları
  const archiveExtensions = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'iso'];
  if (archiveExtensions.includes(extension)) {
    return {
      Icon: FileArchive,
      color: 'text-yellow-600',
      bgColor: 'from-yellow-100 to-amber-100',
      label: 'Arşiv'
    };
  }

  // Video dosyaları
  const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'm4v', 'mpg', 'mpeg'];
  if (videoExtensions.includes(extension)) {
    return {
      Icon: FileVideo,
      color: 'text-pink-600',
      bgColor: 'from-pink-100 to-rose-100',
      label: 'Video'
    };
  }

  // Ses dosyaları
  const audioExtensions = ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma', 'opus'];
  if (audioExtensions.includes(extension)) {
    return {
      Icon: FileAudio,
      color: 'text-indigo-600',
      bgColor: 'from-indigo-100 to-purple-100',
      label: 'Ses'
    };
  }

  // Metin dosyaları
  const textExtensions = ['txt', 'md', 'log', 'readme'];
  if (textExtensions.includes(extension)) {
    return {
      Icon: FileText,
      color: 'text-gray-600',
      bgColor: 'from-gray-100 to-slate-100',
      label: 'Metin'
    };
  }

  // Bilinmeyen/Genel dosya
  return {
    Icon: File,
    color: 'text-gray-600',
    bgColor: 'from-gray-100 to-gray-200',
    label: 'Dosya'
  };
};

/**
 * Dosya uzantısını döndürür
 * @param {string} filename - Dosya adı
 * @returns {string} - Uzantı (örn: "pdf", "docx")
 */
export const getFileExtension = (filename) => {
  if (!filename) return '';
  return filename.split('.').pop()?.toLowerCase() || '';
};

/**
 * Dosya uzantısı badge'i için renk döndürür
 * @param {string} filename - Dosya adı
 * @returns {string} - Tailwind CSS sınıfları
 */
export const getExtensionBadgeColor = (filename) => {
  const extension = getFileExtension(filename);
  const { color, bgColor } = getFileIcon(filename);
  
  return {
    text: color.replace('text-', ''),
    bg: bgColor
  };
};
